import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { resolveMediaUrl } from "@/lib/api";
import { useSharedHeroPhotos } from "@/hooks/useSharedHeroPhotos";

/**
 * HeroSlideshow (copy-only variant)
 * ---------------------------------
 * The Hero now renders ONLY its editorial copy + a per-slide adaptive
 * text-panel overlay. The photograph itself is painted by the shared
 * <HomeCinematicBackground /> that lives behind all three home
 * sections — so scrolling from the Hero into the Manifesto never
 * shows a photo "reset" or seam: it's one continuous take, and only
 * the headline changes.
 *
 * The `handoff` state still fades the copy + counter + scroll cue as
 * the visitor scrolls into the Manifesto, but the photograph stays
 * put behind everything.
 */

const AUTO_ADVANCE_MS = 6500; // must match useSharedHeroPhotos AUTO_ADVANCE_MS

/**
 * Sample the average relative luminance of the bottom-left region of an image
 * (where the hero typography sits) and return a value in [0..255]. Falls back to
 * 140 (mid-tone) if the canvas taints or the image fails to load.
 */
function sampleImageLuminance(url) {
  return new Promise((resolve) => {
    if (typeof document === "undefined") return resolve(140);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const size = 32;
        const c = document.createElement("canvas");
        c.width = size;
        c.height = size;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, size, size);
        const data = ctx.getImageData(0, 0, size, size).data;
        let sum = 0;
        let n = 0;
        const yStart = Math.floor(size * 0.45);
        const xEnd = Math.floor(size * 0.6);
        for (let y = yStart; y < size; y++) {
          for (let x = 0; x < xEnd; x++) {
            const i = (y * size + x) * 4;
            sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
            n++;
          }
        }
        resolve(n > 0 ? sum / n : 140);
      } catch {
        resolve(140);
      }
    };
    img.onerror = () => resolve(140);
    img.src = url;
  });
}

/**
 * Map bottom-left luminance (0..255) to the overlay layer's opacity (0.18..0.82).
 * Brighter images get a stronger overlay; dark images stay almost natural.
 */
function overlayOpacityFor(lum) {
  if (typeof lum !== "number" || Number.isNaN(lum)) return 0.5;
  const t = Math.min(1, Math.max(0, lum / 255));
  return Math.max(0.18, Math.min(0.82, 0.18 + t * 0.64));
}

export function HeroSlideshow() {
  const { photos, current, next, prev } = useSharedHeroPhotos();
  const [progress, setProgress] = useState(0);
  const [brightnessMap, setBrightnessMap] = useState({});
  const [handoff, setHandoff] = useState(0);
  const brightnessRef = useRef({});

  // Sample luminance for the currently-showing photo so the adaptive text
  // panel can auto-adjust opacity per slide (bright photos → stronger wash).
  useEffect(() => {
    photos.forEach((p) => {
      if (brightnessRef.current[p.id] !== undefined) return;
      brightnessRef.current[p.id] = null;
      sampleImageLuminance(resolveMediaUrl(p.image_url)).then((lum) => {
        brightnessRef.current[p.id] = lum;
        setBrightnessMap((m) => ({ ...m, [p.id]: lum }));
      });
    });
  }, [photos]);

  const total = photos.length;

  // Progress bar — visual only. The store's timer drives the real advance.
  useEffect(() => {
    setProgress(0);
    if (total <= 1) return;
    const start = Date.now();
    const id = setInterval(() => {
      const p = Math.min((Date.now() - start) / AUTO_ADVANCE_MS, 1);
      setProgress(p);
      if (p >= 1) clearInterval(id);
    }, 60);
    return () => clearInterval(id);
  }, [current, total]);

  // Handoff — copy layer fades as the visitor scrolls the last half of
  // the Hero section. The photograph stays visible (it's on the shared
  // sticky background), so the visitor perceives one continuous scene:
  // only the headline vacates the frame to make room for the manifesto.
  useEffect(() => {
    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        const vh = window.innerHeight || 1;
        const t = Math.min(1, Math.max(0, (window.scrollY - vh * 0.45) / (vh * 0.55)));
        setHandoff(t);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  const scrollPastHero = () => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  const currentLum = brightnessMap[photos[current]?.id];

  return (
    <section
      data-testid="hero-slideshow"
      className="relative w-full h-screen bg-transparent"
      aria-label="Studio gallery slideshow"
    >
      {/* Constant vertical vignette — protects nav (top) and progress
          (bottom) regardless of image brightness. Kept subtle so the
          photograph feels natural. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,12,11,0.42) 0%, rgba(15,12,11,0.00) 22%, rgba(15,12,11,0.00) 68%, rgba(15,12,11,0.32) 100%)",
        }}
      />

      {/* Adaptive text-panel — anchored to the bottom-left where the copy
          lives; opacity modulated per-slide brightness AND per-scroll so
          it dissolves in sync with the copy. */}
      <div
        data-testid="hero-adaptive-overlay"
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(100deg, rgba(15,12,11,1) 0%, rgba(15,12,11,0.75) 26%, rgba(15,12,11,0.28) 56%, rgba(15,12,11,0) 82%)",
          opacity: overlayOpacityFor(currentLum) * (1 - handoff),
          transition: "opacity 1200ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      />

      {/* Copy layer — the only thing that fades during the handoff. */}
      <div
        className="absolute inset-0 flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-28 md:pb-36 z-10 pointer-events-none"
        style={{
          opacity: 1 - handoff,
          transform: `translate3d(0, ${handoff * -18}px, 0)`,
          transition: "opacity 180ms linear, transform 180ms linear",
          willChange: "opacity, transform",
        }}
      >
        <div className="max-w-3xl pointer-events-auto">
          <p
            data-testid="hero-eyebrow"
            className="text-[10px] md:text-[11px] tracking-[0.42em] uppercase text-ink-champagne"
          >
            Cineglimpse Stories
          </p>
          <h1
            data-testid="hero-headline"
            className="mt-6 md:mt-8 font-display text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.92] tracking-[-0.02em] text-ink-ivory"
          >
            Films that become<br />
            <span className="italic font-light text-ink-champagne">family heirlooms.</span>
          </h1>
          <p
            data-testid="hero-description"
            className="mt-8 md:mt-10 max-w-xl text-base md:text-lg leading-relaxed text-ink-ivory/85"
          >
            A cinematic archive of weddings, where every frame preserves
            emotion, intimacy, and the beauty of moments that never return.
          </p>
          <div className="mt-10 md:mt-12 flex flex-wrap items-center gap-x-8 gap-y-4">
            <Link to="/films" className="luxe-btn" data-testid="hero-cta-films">
              View Films
            </Link>
            <Link
              to="/contact"
              className="text-[11px] tracking-[0.28em] uppercase text-ink-ivory editorial-link"
              data-testid="hero-cta-contact"
            >
              Begin Your Story →
            </Link>
          </div>
        </div>
      </div>

      {/* Counter — bottom-right */}
      {total > 0 && (
        <div
          data-testid="hero-counter"
          className="absolute bottom-10 md:bottom-12 right-6 md:right-12 lg:right-16 z-20 pointer-events-none text-right"
          style={{ opacity: 1 - handoff, transition: "opacity 180ms linear" }}
        >
          <p className="text-[10px] tracking-[0.42em] uppercase text-ink-ivory/85">
            <span data-testid="hero-counter-current" className="text-ink-champagne">
              {String(current + 1).padStart(2, "0")}
            </span>
            <span className="mx-2 text-ink-ivory/40">/</span>
            <span data-testid="hero-counter-total">{String(total).padStart(2, "0")}</span>
          </p>
        </div>
      )}

      {/* Scroll cue — bottom-center */}
      <button
        type="button"
        onClick={scrollPastHero}
        data-testid="hero-scroll-cue"
        aria-label="Scroll past hero"
        className="absolute bottom-10 md:bottom-12 left-1/2 -translate-x-1/2 z-20 group flex flex-col items-center gap-2 pointer-events-auto"
        style={{ opacity: 1 - handoff, transition: "opacity 180ms linear" }}
      >
        <span className="text-[9px] tracking-[0.42em] uppercase text-ink-ivory/70 group-hover:text-ink-champagne transition-colors duration-500">
          Continue
        </span>
        <span aria-hidden className="block w-px h-8 bg-ink-ivory/30 overflow-hidden">
          <span
            className="block w-px bg-ink-champagne"
            style={{ height: `${progress * 100}%`, transition: "height 60ms linear" }}
          />
        </span>
      </button>

      {/* Prev / Next click zones (kept invisible for keyboard-free left/right taps) */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous slide"
            data-testid="hero-prev"
            className="absolute inset-y-0 left-0 w-1/6 z-20 pointer-events-auto cursor-w-resize"
          />
          <button
            type="button"
            onClick={next}
            aria-label="Next slide"
            data-testid="hero-next"
            className="absolute inset-y-0 right-0 w-1/6 z-20 pointer-events-auto cursor-e-resize"
          />
        </>
      )}
    </section>
  );
}
